/**
 * Tournament Utility Functions
 * 
 * This module provides utility functions for managing tournaments
 */

/**
 * Calculate the bracket size based on number of participants
 * Rounds up to the nearest power of 2
 * @param {number} numParticipants - Number of participants in the tournament
 * @returns {number} - The bracket size (next power of 2)
 */
export const calculateBracketSize = (numParticipants) => {
  // Find the next power of 2
  return Math.pow(2, Math.ceil(Math.log2(numParticipants)));
};

/**
 * Generate initial tournament bracket matches
 * @param {Array} players - Array of player objects
 * @returns {Array} - Array of match objects
 */
export const generateInitialBracket = (players) => {
  const bracketSize = calculateBracketSize(players.length);
  const matches = [];
  const numByes = bracketSize - players.length;
  
  // Create a copy of players and shuffle them
  const shuffledPlayers = [...players].sort(() => Math.random() - 0.5);
  
  // Distribute byes evenly - place byes at the end
  const playersWithByes = [...shuffledPlayers];
  for (let i = 0; i < numByes; i++) {
    playersWithByes.push(null); // null represents a bye
  }
  
  // Create initial round matches
  for (let i = 0; i < bracketSize / 2; i++) {
    matches.push({
      match_id: i + 1,
      round: 1,
      player1_id: playersWithByes[i * 2]?.id || null,
      player2_id: playersWithByes[i * 2 + 1]?.id || null,
      winner_id: null,
      loser_id: null,
      score: null,
      completed: false,
      next_match_id: Math.floor(i / 2) + Math.floor(bracketSize / 4) + 1,
      // If either player is null (bye), the other player automatically advances
      bye: playersWithByes[i * 2] === null || playersWithByes[i * 2 + 1] === null
    });
  }
  
  // Create placeholder matches for subsequent rounds
  let currentRound = 2;
  let matchId = Math.floor(bracketSize / 2) + 1;
  let remainingMatches = Math.floor(bracketSize / 4);
  
  while (remainingMatches > 0) {
    for (let i = 0; i < remainingMatches; i++) {
      matches.push({
        match_id: matchId,
        round: currentRound,
        player1_id: null,
        player2_id: null,
        winner_id: null,
        loser_id: null,
        score: null,
        completed: false,
        next_match_id: remainingMatches > 1 ? Math.floor(i / 2) + matchId + remainingMatches : null,
        bye: false
      });
      matchId++;
    }
    
    currentRound++;
    remainingMatches = Math.floor(remainingMatches / 2);
  }
  
  return matches;
};

/**
 * Process an automatic advancement due to a bye
 * @param {Array} matches - All tournament matches
 * @param {number} matchId - The match ID to process
 * @returns {Array} - Updated matches array
 */
export const processAutomaticAdvancement = (matches, matchId) => {
  const match = matches.find(m => m.match_id === matchId);
  if (!match || !match.bye) return matches;
  
  // Determine the advancing player (the non-null one)
  const winnerId = match.player1_id || match.player2_id;
  const updatedMatches = matches.map(m => {
    if (m.match_id === matchId) {
      return {
        ...m,
        winner_id: winnerId,
        loser_id: null,
        completed: true,
        score: "BYE"
      };
    }
    
    // Update the next match with the advancing player
    if (m.match_id === match.next_match_id) {
      // Place the winner in the correct position based on whether this was an odd or even match
      const isEvenMatch = matchId % 2 === 0;
      return {
        ...m,
        player1_id: isEvenMatch ? winnerId : m.player1_id,
        player2_id: isEvenMatch ? m.player2_id : winnerId
      };
    }
    
    return m;
  });
  
  return updatedMatches;
};

/**
 * Process tournament match result
 * @param {Array} matches - All tournament matches
 * @param {Object} matchResult - Match result object
 * @returns {Array} - Updated matches array
 */
export const processMatchResult = (matches, matchResult) => {
  const { matchId, player1Score, player2Score, winnerId, loserId } = matchResult;
  
  return matches.map(match => {
    if (match.match_id === matchId) {
      return {
        ...match,
        winner_id: winnerId,
        loser_id: loserId,
        score: `${player1Score}-${player2Score}`,
        completed: true
      };
    }
    
    // Update the next match with the advancing player
    if (match.match_id === matches.find(m => m.match_id === matchId)?.next_match_id) {
      // Place the winner in the correct position based on whether this was an odd or even match
      const isEvenMatch = matchId % 2 === 0;
      return {
        ...match,
        player1_id: isEvenMatch ? winnerId : match.player1_id,
        player2_id: isEvenMatch ? match.player2_id : winnerId
      };
    }
    
    return match;
  });
};

/**
 * Check if a tournament is completed
 * @param {Array} matches - All tournament matches
 * @returns {boolean} - Whether the tournament is completed
 */
export const isTournamentCompleted = (matches) => {
  // Find the final match (the one with no next_match_id)
  const finalMatch = matches.find(match => match.next_match_id === null);
  return finalMatch && finalMatch.completed;
};

/**
 * Get tournament winner
 * @param {Array} matches - All tournament matches
 * @returns {string|null} - Winner ID or null if tournament not completed
 */
export const getTournamentWinner = (matches) => {
  const finalMatch = matches.find(match => match.next_match_id === null);
  return finalMatch && finalMatch.completed ? finalMatch.winner_id : null;
}; 